import * as React from 'react';
import * as _ from 'underscore';

import { IValuesCollection } from '../../ts/appTheme';
import { themeVars } from '../../../../pdc-platform/src/ts/themes/themeConstants';
import * as Globalization from '../../../../_common/platform/ts/Globalization';

export const CLEAR_SELECTIONS_VALUE = 'clear-selections-item';
export const SHOW_MORE_VALUE = 'show-more-item';

export function HiddenItemsButton(props: {
    numberToShow: number;
    title: string;
    theme: IValuesCollection;
    onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
}) {
    const { theme, numberToShow } = props;

    if (numberToShow == null) {
        return null;
    }

    let hiddenItemsButton: React.JSX.Element;

    if (numberToShow > 0) {
        const buttonStyle: React.CSSProperties = {};

        if (theme != null) {
            buttonStyle.background = theme[themeVars.itemSelector_SelectedHiddenItemBackgroundColor] || 'lightgray';

            const foregroundColor = theme[themeVars.itemSelector_SelectedItemForegroundColor];

            if (foregroundColor != null) {
                buttonStyle.color = foregroundColor;
            }
        }

        let hiddenElementsText: string;

        if (numberToShow < 1000) {
            hiddenElementsText = '+' + numberToShow.toString();
        } else if (numberToShow >= 1000 || numberToShow < 10000) {
            hiddenElementsText = numberToShow.toString();
        } else {
            hiddenElementsText = 'more';
        }

        const handleOnClick = (e) => {
            if (props.onClick != null) {
                e.stopPropagation();
                e.preventDefault();

                props.onClick(e);
            }
        };

        hiddenItemsButton = (
            <div className="hidden-items-button" title={props.title} style={buttonStyle} onClick={handleOnClick}>
                {hiddenElementsText}
            </div>
        );
    }

    return <td className="item-selector-hidden-items">{hiddenItemsButton}</td>;
}

export const REMOVE_CELL_WIDTH = 16;

export function RemoveItemButton(props: { onClick?: React.MouseEventHandler<any> | undefined }) {
    return (
        <td className="value-remove-cell" style={{ width: REMOVE_CELL_WIDTH + 'px' }}>
            <span
                className="glyphicon glyphicon-remove"
                onClick={(e) => {
                    if (props.onClick != null) {
                        e.stopPropagation();
                        props.onClick(e);
                    }
                }}
            ></span>
        </td>
    );
}

export function SearchField(props: {
    reference: React.RefObject<HTMLInputElement>;
    maxLength?: number;
    placeholder?: string;
    defaultValue?: string;

    onFocus?(e): void;
    onBlur?(e): void;
    onChange?(value: string): void;
}) {
    const handleOnSearchChange = () => {
        if (props.onChange != null) {
            props.onChange(props.reference.current.value);
        }
    };

    const handleOnClearClick = () => {
        props.reference.current.value = '';

        if (props.onChange != null) {
            props.onChange('');
        }
    };

    return (
        <div className="search-field">
            <table className={'table-borderless w-100'}>
                <tbody>
                    <tr>
                        <td className="text-cell">
                            <input
                                type="text"
                                className="search-field-input"
                                ref={props.reference}
                                maxLength={props.maxLength}
                                placeholder={props.placeholder || Globalization.getMessage('searchFieldPlaceHolder')}
                                defaultValue={props.defaultValue || ''}
                                onFocus={props.onFocus}
                                onBlur={props.onBlur}
                                onInput={handleOnSearchChange}
                            />
                        </td>

                        <td className="button-cell">
                            <span
                                className="glyphicon glyphicon-remove"
                                title={Globalization.getMessage('clearSelections')}
                                onClick={handleOnClearClick}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

export function SelectedItemsPlaceholder(props: { text?: string; maxWidth?: number }) {
    let titleClass = 'placeholder';

    if (props.text?.length > 0) {
        const maxWidth = props.maxWidth > 0 ? props.maxWidth - 9 : null;

        let titleStyle: React.CSSProperties;

        if (maxWidth > 0) {
            titleClass += ' limited-text';
            titleStyle = {
                maxWidth: maxWidth + 'px'
            };
        }

        return (
            <div className={titleClass} style={titleStyle}>
                {props.text}
            </div>
        );
    }

    return <div className={titleClass}>&nbsp;</div>;
}

export function EmptyPlaceholder(props: { fontWidth: React.RefObject<number> }) {
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const fontSize = getComputedStyle(ref.current).fontSize;

        if (fontSize != null && fontSize.length > 2) {
            const fontWidth = parseFloat(fontSize.substring(0, fontSize.length - 2));

            props.fontWidth.current = !isNaN(fontWidth) ? fontWidth : null;
        }
    }, []);

    return (
        <div ref={ref} className={'placeholder'} style={{ width: '1rem' }}>
            &nbsp;
        </div>
    );
}

export interface IMatch {
    begining?: string;
    exactMatch?: string;
    ending?: string;
}

export function TitleOfMatch(props: { match: IMatch; highlightingColor: string; className?: string }) {
    const { begining, exactMatch, ending } = props.match;

    return (
        <span className={'title-of-match' + (props.className != null ? ' ' + props.className : '')}>
            {begining != null && begining !== '' && <span>{begining}</span>}
            {exactMatch != null && exactMatch !== '' && (
                <span style={props.highlightingColor != null ? { background: props.highlightingColor } : undefined}>
                    {exactMatch}
                </span>
            )}
            {ending != null && ending !== '' && <span>{ending}</span>}
        </span>
    );
}

export function NoItemElement() {
    return <em>{'No item is found'}</em>;
}

export function ShowMoreElement(props: {
    className: string;
    numberOfHiddenItems: number;
    onClick(e: React.MouseEvent<HTMLDivElement>): void;
}) {
    return (
        <div className={props.className} onClick={props.onClick}>
            {props.numberOfHiddenItems > 1
                ? `Show more (${props.numberOfHiddenItems} are hidden)`
                : `Show more (${props.numberOfHiddenItems} is hidden)`}
        </div>
    );
}

export function getSearchHighlightingColor(theme: IValuesCollection) {
    return theme?.[themeVars.itemSelector_SearchHighlightingColor] ?? 'yellow';
}
